import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { RealtimeChannel } from '@supabase/supabase-js';

type PostgresEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

interface SubscriptionPayload {
  new: Record<string, unknown>;
  old: Record<string, unknown>;
  eventType: string;
}

interface TableSubscription {
  table: string;
  event?: PostgresEvent;
  filter?: string;
  onData?: (payload: SubscriptionPayload) => void;
  invalidateQueries?: string[][];
  showToast?: {
    title: string | ((payload: Record<string, unknown>) => string);
    description?: string | ((payload: Record<string, unknown>) => string);
    variant?: 'default' | 'destructive';
  };
}

interface UseRealtimeSubscriptionOptions {
  channelName: string;
  enabled?: boolean;
  tables: TableSubscription[];
}

/**
 * Reusable hook for Supabase real-time subscriptions
 * Consolidates multiple table subscriptions into a single channel
 * Handles cleanup, cache invalidation, and toast notifications
 */
export function useRealtimeSubscription({
  channelName,
  enabled = true,
  tables,
}: UseRealtimeSubscriptionOptions) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const channelRef = useRef<RealtimeChannel | null>(null);

  // Stable refs to prevent recreating subscriptions
  const tablesRef = useRef(tables);
  tablesRef.current = tables;

  const handlePayload = useCallback(
    (subscription: TableSubscription, payload: SubscriptionPayload) => {
      const newData = payload.new;

      // Call custom handler
      subscription.onData?.(payload);

      // Invalidate queries
      if (subscription.invalidateQueries) {
        subscription.invalidateQueries.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }

      // Show toast
      if (subscription.showToast && newData) {
        const title =
          typeof subscription.showToast.title === 'function'
            ? subscription.showToast.title(newData)
            : subscription.showToast.title;

        const description =
          typeof subscription.showToast.description === 'function'
            ? subscription.showToast.description(newData)
            : subscription.showToast.description;

        toast({
          title,
          description,
          variant: subscription.showToast.variant || 'default',
        });
      }
    },
    [queryClient, toast]
  );

  useEffect(() => {
    if (!enabled || !channelName) return;

    // Build channel with all subscriptions using the fluent API
    let channel = supabase.channel(channelName);

    tablesRef.current.forEach((subscription, index) => {
      // Use type assertion to work around strict typing
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      channel = (channel as any).on(
        'postgres_changes',
        {
          event: subscription.event || '*',
          schema: 'public',
          table: subscription.table,
          filter: subscription.filter,
        },
        (payload: SubscriptionPayload) => handlePayload(subscription, payload)
      );
    });

    // Subscribe to channel
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log(`[Realtime] Subscribed to ${channelName}`);
      } else if (status === 'CHANNEL_ERROR') {
        console.error(`[Realtime] Error subscribing to ${channelName}`);
      }
    });

    channelRef.current = channel;

    return () => {
      console.log(`[Realtime] Unsubscribing from ${channelName}`);
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [channelName, enabled, handlePayload]);

  return {
    channel: channelRef.current,
  };
}

/**
 * Hook for auth state change with redirect
 * Consolidates duplicate auth listeners across dashboards
 */
export function useAuthRedirect(onSignOut?: () => void) {
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        onSignOut?.();
      }
    });

    return () => subscription.unsubscribe();
  }, [onSignOut]);
}
